import React, { useMemo } from 'react';
import { usePromoKeranjang } from '../../promo/usePromoKeranjang';
import {
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ShoppingCart,
  Add,
  Remove,
  Delete,
  CreditCard,
} from '@mui/icons-material';

const bs3 = {
  panelDefault: {
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxShadow: 'none',
    mb: 2,
    bgcolor: '#fff',
  },
  panelHeading: (color = '#f5f5f5', textColor = '#333') => ({
    bgcolor: color,
    color: textColor,
    px: 2,
    py: 1.2,
    borderBottom: '1px solid #ddd',
    borderRadius: '4px 4px 0 0',
    fontWeight: 700,
    fontSize: '14px',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  }),
  btnSuccess: {
    bgcolor: '#5cb85c',
    color: '#fff',
    border: '1px solid #4cae4c',
    borderRadius: '4px',
    px: 2,
    py: 0.8,
    fontSize: '13px',
    fontWeight: 400,
    textTransform: 'none',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    '&:hover': { bgcolor: '#449d44', borderColor: '#398439' },
  },
  formControl: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
      fontSize: '13px',
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      '& fieldset': { borderColor: '#ccc' },
      '&:hover fieldset': { borderColor: '#66afe9' },
      '&.Mui-focused fieldset': {
        borderColor: '#66afe9',
        boxShadow: 'inset 0 1px 1px rgba(0,0,0,.075), 0 0 8px rgba(102,175,233,.6)',
      },
    },
  },
  label: {
    fontSize: '13px',
    fontWeight: 700,
    color: '#333',
    mb: 0.5,
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  badge: (bg = '#337ab7') => ({
    display: 'inline-block',
    bgcolor: bg,
    color: '#fff',
    fontSize: '11px',
    fontWeight: 700,
    px: 1,
    py: 0.2,
    borderRadius: '10px',
    lineHeight: 1.4,
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  }),
  tableStriped: {
    '& .MuiTableRow-root:nth-of-type(odd)': { bgcolor: '#f9f9f9' },
    '& .MuiTableRow-root:hover': { bgcolor: '#f5f5f5' },
    '& .MuiTableCell-root': {
      fontSize: '13px',
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      py: 1,
      px: 1,
      borderBottom: '1px solid #ddd',
    },
    '& .MuiTableCell-head': {
      fontWeight: 700,
      bgcolor: '#f5f5f5',
      borderBottom: '2px solid #ddd',
      fontSize: '12px',
      textTransform: 'uppercase',
      color: '#555',
    },
  },
  alertInfo: {
    bgcolor: '#d9edf7',
    color: '#31708f',
    border: '1px solid #bce8f1',
    borderRadius: '4px',
    p: 1.5,
    fontSize: '13px',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  },
};

/**
 * CartRetail — Komponen keranjang belanja untuk kasir retail.
 * Menerima state dari parent (controlled component).
 *
 * Props:
 *   cart                     : array — daftar item di keranjang
 *   namaCustomer             : string
 *   setNamaCustomer          : setter
 *   noted                    : string
 *   setNoted                 : setter
 *   selectedPaymentMethod    : string
 *   setSelectedPaymentMethod : setter
 *   onIncrement              : (item) => void
 *   onDecrement              : (item) => void
 *   onRemove                 : (itemKey) => void
 *   onCheckout               : (payload) => void
 *   fixed                    : boolean — apakah posisi fixed/sticky (default: true)
 *   printerConnected         : boolean — status koneksi printer bluetooth
 */
const CartRetail = ({
  cart = [],
  namaCustomer = '',
  setNamaCustomer,
  noted = '',
  setNoted,
  selectedPaymentMethod = 'cash',
  setSelectedPaymentMethod,
  onIncrement,
  onDecrement,
  onRemove,
  onCheckout,
  fixed = true,
  printerConnected = false,
  qrisEnabled = true,
}) => {
  const bruto = useMemo(
    () => cart.reduce((sum, item) => sum + (parseFloat(item.harga) || 0) * item.quantity, 0),
    [cart]
  );
  // Promo otomatis (server menghitung ulang saat transaksi disimpan); subtotal = total belanja SETELAH promo.
  const { diskonTotal: diskonPromo, promoNama } = usePromoKeranjang(
    cart.map((item) => ({ baranguuid: item.baranguuid, total: (parseFloat(item.harga) || 0) * item.quantity }))
  );
  const subtotal = Math.max(0, bruto - diskonPromo);

  return (
    <Box
      sx={fixed ? {
        position: { md: 'sticky' },
        top: { md: 16 },
        maxHeight: { md: 'calc(100vh - 32px)' },
        overflow: { md: 'auto' },
        ...bs3.panelDefault,
      } : bs3.panelDefault}
    
    >
      {/* Header */}
      <Box sx={bs3.panelHeading('#337ab7', '#fff')}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShoppingCart sx={{ fontSize: 18 }} />
          <span>Keranjang</span>
          <Box sx={bs3.badge('#fff')}>
            <span style={{ color: '#337ab7' }}>{cart.length}</span>
          </Box>
        </Box>
        <Box sx={bs3.badge('#5cb85c')}>
          Rp {subtotal.toLocaleString('id-ID')}
        </Box>
      </Box>

      <Box sx={{ p: 0 }}>
        {cart.length === 0 ? (
          <Box sx={{ ...bs3.alertInfo, m: 2, textAlign: 'center' }}>
            Belum ada barang di keranjang.
          </Box>
        ) : (
          <Table size="small" sx={bs3.tableStriped}>
            <TableHead>
              <TableRow>
                <TableCell>Barang</TableCell>
                <TableCell align="center">Qty</TableCell>
                <TableCell align="right">Subtotal</TableCell>
                <TableCell align="center" sx={{ width: 80 }}>Aksi</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cart.map((item) => (
                <TableRow key={item.key}>
                  <TableCell>
                    <Box>
                      <Typography sx={{ fontSize: '12px', fontWeight: 600, lineHeight: 1.3, fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}>
                        {item.nama}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: '#999', fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}>
                        Rp {item.harga.toLocaleString('id-ID')}{item.grosir ? ' • harga grosir' : ''}
                        {item.serialLabels?.length > 0 && (
                          <span style={{ marginLeft: 4, color: '#337ab7' }}>
                            SN: {item.serialLabels.join(', ')}
                          </span>
                        )}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3 }}>
                      <IconButton
                        size="small"
                        onClick={() => onDecrement(item)}
                        sx={{
                          width: 22,
                          height: 22,
                          border: '1px solid #ccc',
                          borderRadius: '3px',
                          fontSize: '14px',
                          '&:hover': { bgcolor: '#e6e6e6' },
                        }}
                      >
                        <Remove sx={{ fontSize: 14 }} />
                      </IconButton>
                      <Typography sx={{ fontSize: '13px', fontWeight: 700, minWidth: 20, textAlign: 'center', fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}>
                        {item.quantity}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => onIncrement(item)}
                        sx={{
                          width: 22,
                          height: 22,
                          border: '1px solid #ccc',
                          borderRadius: '3px',
                          fontSize: '14px',
                          '&:hover': { bgcolor: '#e6e6e6' },
                        }}
                      >
                        <Add sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#337ab7', fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}>
                      Rp {(item.harga * item.quantity).toLocaleString('id-ID')}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => onRemove(item.key)}
                      sx={{
                        width: 24,
                        height: 24,
                        bgcolor: '#d9534f',
                        color: '#fff',
                        borderRadius: '3px',
                        '&:hover': { bgcolor: '#c9302c' },
                      }}
                    >
                      <Delete sx={{ fontSize: 14 }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Total Section */}
        {cart.length > 0 && (
          <Box
            sx={{
              bgcolor: '#f5f5f5',
              borderTop: '2px solid #337ab7',
              p: 2,
            }}
          >
            {diskonPromo > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#2e7d32' }}>
                  Promo{promoNama ? `: ${promoNama}` : ''}
                </Typography>
                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#2e7d32' }}>
                  - Rp {diskonPromo.toLocaleString('id-ID')}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#555', fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}>
                Total ({cart.reduce((sum, i) => sum + i.quantity, 0)} item)
              </Typography>
              <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#337ab7', fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}>
                Rp {subtotal.toLocaleString('id-ID')}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Customer & Payment Form */}
        <Box sx={{ p: 2, borderTop: '1px solid #ddd' }}>
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={bs3.label}>Nama Customer</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Opsional..."
              value={namaCustomer}
              onChange={(e) => setNamaCustomer(e.target.value)}
              sx={bs3.formControl}
            />
          </Box>

          <Box sx={{ mb: 1.5 }}>
            <Typography sx={bs3.label}>Catatan</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Catatan transaksi..."
              value={noted}
              onChange={(e) => setNoted(e.target.value)}
              sx={bs3.formControl}
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography sx={bs3.label}>Metode Pembayaran</Typography>
            <TextField
              select
              fullWidth
              size="small"
              value={selectedPaymentMethod}
              onChange={(e) => setSelectedPaymentMethod(e.target.value)}
              sx={bs3.formControl}
            >
              <MenuItem value="cash">Tunai (Cash)</MenuItem>
              {qrisEnabled && <MenuItem value="qris">QRIS</MenuItem>}
              <MenuItem value="noncash">Non Cash</MenuItem>
              <MenuItem value="bagi">Bagi Pembayaran (Tunai + Non Cash)</MenuItem>
              <MenuItem value="dp">Uang Muka (DP) / Piutang</MenuItem>
            </TextField>
            <Box sx={{ mt: 1 }}>
              <Chip
                size="small"
                label={printerConnected ? 'Auto print Bluetooth aktif' : 'Auto print nonaktif (fallback browser)'}
                color={printerConnected ? 'success' : 'default'}
                variant="outlined"
              />
            </Box>
          </Box>

          {/* Checkout Button */}
          <Button
            fullWidth
            startIcon={<CreditCard sx={{ fontSize: 18 }} />}
            onClick={() =>
              onCheckout?.({
                subtotal,
                itemCount: cart.reduce((sum, i) => sum + i.quantity, 0),
                selectedPaymentMethod,
                namaCustomer,
                noted,
              })
            }
            disabled={cart.length === 0}
            sx={{
              ...bs3.btnSuccess,
              py: 1.2,
              fontSize: '15px',
              fontWeight: 700,
              letterSpacing: 0.5,
            }}
          >
            Checkout - Rp {subtotal.toLocaleString('id-ID')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CartRetail;
